import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, euro, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Crown, Check, Sparkles } from "lucide-react";

const TRIAL_BENEFITS = [
  "Menus IA illimités avec budget & équilibre nutritionnel",
  "Historique de prix complet et graphiques détaillés",
  "Alertes email de baisse de prix",
  "Paniers famille & collaboratifs en temps réel",
];

export default function Register() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("form");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState("premium_monthly");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const monthly = plans.find((p) => p.lookup_key === "premium_monthly");
  const yearly = plans.find((p) => p.lookup_key === "premium_yearly");
  const yearlySavePct = monthly && yearly ? Math.round((1 - yearly.amount / (monthly.amount * 12)) * 100) : 0;
  const current = plans.find((p) => p.lookup_key === selected);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      login(data.token, data.user);
      setNewUser(data.user);
      setStep("premium");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const startTrial = async () => {
    if (!newUser) return;
    setStarting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected,
        origin_url: window.location.origin,
        user_id: newUser.id,
        ref: localStorage.getItem("pmc_ref") || undefined,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Impossible de démarrer l'essai");
      setStarting(false);
    }
  };

  const skip = () => nav("/comparateur");

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 px-4 py-10">
      <AnimatePresence mode="wait">
        {step === "form" ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm"
          >
            <Link to="/" className="mb-6 flex items-center gap-2">
              <img src="/logo192.png" alt="PanierMoinsCher" className="h-9 w-9 rounded-xl shadow-sm" />
              <span className="font-display text-lg font-800 text-secondary">PanierMoinsCher</span>
            </Link>
            <h1 className="font-display text-2xl font-800 text-secondary">Créer un compte</h1>
            <p className="mt-1 text-sm text-muted-foreground">Gratuit — pour sauvegarder vos paniers et alertes.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
              <div>
                <Label htmlFor="name">Nom</Label>
                <Input id="name" value={name} required onChange={(e) => setName(e.target.value)} data-testid="register-name" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} required onChange={(e) => setEmail(e.target.value)} data-testid="register-email" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input id="password" type="password" value={password} required minLength={6} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="mt-1.5" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="register-submit">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Créer mon compte
              </Button>
            </form>
            <p className="mt-5 text-center text-sm text-muted-foreground">
              Déjà inscrit ?{" "}
              <Link to="/connexion" className="font-semibold text-primary hover:underline" data-testid="link-login">
                Se connecter
              </Link>
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="premium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm"
            data-testid="register-premium-step"
          >
            <div className="text-center">
              <Badge className="mb-3 rounded-full bg-accent/10 text-accent" data-testid="trial-badge">
                <Sparkles className="mr-1 h-3 w-3" /> Essai gratuit de 7 jours
              </Badge>
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white">
                <Crown className="h-6 w-6" />
              </div>
              <h1 className="font-display text-2xl font-800 text-secondary">Bienvenue {newUser?.name?.split(" ")[0] || ""} !</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Démarrez votre essai Premium. Aucun débit pendant 7 jours, résiliable à tout moment.
              </p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {[monthly, yearly].filter(Boolean).map((p) => (
                <button
                  key={p.lookup_key}
                  onClick={() => setSelected(p.lookup_key)}
                  className={`relative rounded-xl border-2 p-4 text-left transition-all ${selected === p.lookup_key ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                  data-testid={`register-plan-${p.lookup_key}`}
                >
                  {p.lookup_key === "premium_yearly" && yearlySavePct > 0 && (
                    <Badge className="absolute -top-2 right-2 bg-accent text-white">-{yearlySavePct}%</Badge>
                  )}
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{p.label}</p>
                  <p className="mt-1 font-display text-2xl font-800 text-secondary">{euro(p.amount)}</p>
                  <p className="text-xs text-muted-foreground">
                    / {p.period}{p.lookup_key === "premium_yearly" ? ` · ${euro(p.amount / 12)}/mois` : ""}
                  </p>
                </button>
              ))}
            </div>
            <p className="mt-3 text-sm text-primary">
              7 jours gratuits, puis {current ? euro(current.amount) : "2,99 €"} / {current?.period || "mois"}
            </p>

            <ul className="mt-5 space-y-2.5">
              {TRIAL_BENEFITS.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-secondary" data-testid={`trial-benefit-${i}`}>
                  <Check className="mt-0.5 h-4 w-4 flex-none text-primary" /> {b}
                </li>
              ))}
            </ul>

            <Button onClick={startTrial} disabled={starting} className="mt-6 w-full rounded-full text-base" size="lg" data-testid="register-start-trial-btn">
              {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Commencer l'essai gratuit
            </Button>
            <button onClick={skip} className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-secondary" data-testid="register-skip-trial-btn">
              Peut-être plus tard — continuer en gratuit
            </button>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Paiement sécurisé par Stripe. Carte de test : 4242 4242 4242 4242. Votre carte est enregistrée et débitée automatiquement à la fin de l'essai.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
